import { Buffer } from 'buffer';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Keypair, Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { supabase } from './supabase';
import { useEffectiveUser } from './ImpersonationContext';

window.Buffer = Buffer;
const SIM_KEY = 'sim-wallet-balance';
function getHeliusRpcUrl() {
  const key = localStorage.getItem('helius-api-key') || process.env.REACT_APP_HELIUS_API_KEY || '';
  return key ? `https://mainnet.helius-rpc.com/?api-key=${key}` : 'https://api.mainnet-beta.solana.com';
}
const VPS_API = 'https://copyccxx.com/api';

const SIM_WALLET = {
  id: 'sim', name: 'Sim Wallet',
  address: 'SimuLation1111111111111111111111111111111111',
  encrypted: null, isSim: true,
};

function loadSimBalance() {
  try { return parseFloat(localStorage.getItem(SIM_KEY) || '10'); } catch { return 10; }
}
function saveSimBalance(bal) {
  try { localStorage.setItem(SIM_KEY, String(bal)); } catch {}
}

export function useBotWallet() {
  const { isImpersonating } = useEffectiveUser();

  const [walletList, setWalletList] = useState([SIM_WALLET]);
  const [activeId, setActiveId] = useState('sim');
  const [keypairs, setKeypairs] = useState({});
  const [balances, setBalances] = useState({ sim: loadSimBalance() });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);

  const connRef = useRef(null);
  function getOrCreateConnection() {
    const url = getHeliusRpcUrl();
    if (!connRef.current || connRef.current.rpcEndpoint !== url) {
      connRef.current = new Connection(url, 'confirmed');
    }
    return connRef.current;
  }

  //  Charger les wallets depuis Supabase au login
  useEffect(() => {
    const loadWallets = async () => {
      if (isImpersonating) {
        setWalletList([SIM_WALLET]);
        setActiveId('sim');
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      setUserId(session.user.id);
      try {
        const r = await fetch(`${VPS_API}/wallet/${session.user.id}`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        });
        const j = await r.json();
        if (j.success && j.wallets?.length > 0) {
          const vpsWallets = j.wallets.map(w => ({
            id: w.id, name: w.name, address: w.address,
            sol_balance: w.sol_balance, isSim: false, fromVPS: true,
          }));
          setWalletList([SIM_WALLET, ...vpsWallets]);
          setActiveId(vpsWallets[0].id);
          const bals = { sim: loadSimBalance() };
          for (const w of vpsWallets) bals[w.id] = w.sol_balance || 0;
          setBalances(bals);
        }
      } catch (e) {
        console.warn('Erreur chargement wallets VPS:', e.message);
      }
    };
    loadWallets();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session?.user && !isImpersonating) loadWallets();
      else if (!session?.user) { setWalletList([SIM_WALLET]); setActiveId('sim'); }
    });
    return () => subscription.unsubscribe();
  }, [isImpersonating]);

  //  Rafraîchir soldes
  useEffect(() => {
    const simRefresh = setInterval(() => {
      setBalances(prev => ({ ...prev, sim: loadSimBalance() }));
    }, 500);

    const realRefresh = async () => {
      const realWallets = walletList.filter(w => !w.isSim && w.fromVPS);
      for (const w of realWallets) {
        try {
          const conn = getOrCreateConnection();
          const bal = await conn.getBalance(new PublicKey(w.address));
          setBalances(prev => ({ ...prev, [w.id]: bal / LAMPORTS_PER_SOL }));
        } catch {}
      }
    };
    realRefresh();
    const realIv = setInterval(realRefresh, 30000);
    return () => { clearInterval(simRefresh); clearInterval(realIv); };
  }, [walletList]);

  const realWallets = walletList.filter(w => !w.isSim);
  const activeWalletObj = walletList.find(w => w.id === activeId) || walletList[0] || null;
  const activeKeypair = activeWalletObj ? keypairs[activeWalletObj.id] || null : null;
  const locked = activeWalletObj?.isSim ? false : activeWalletObj?.fromVPS ? false : !activeKeypair;
  const hasWallet = realWallets.length > 0;
  const isSim = activeWalletObj?.isSim || false;

  const wallet = activeWalletObj ? {
    address: activeWalletObj.address,
    balance: balances[activeWalletObj.id] || 0,
    name: activeWalletObj.name,
    id: activeWalletObj.id,
    isSim: activeWalletObj.isSim || false,
  } : null;

  //  Créer un wallet via VPS API
  const createWallet = useCallback(async (password, name = null) => {
    setLoading(true); setError(null);
    try {
      if (isImpersonating) throw new Error('Action désactivée en mode lecture seule');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Non connecté');
      const walletName = name || ('Wallet Bot '+ (realWallets.length + 1));
      const r = await fetch(`${VPS_API}/wallet/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ userId: session.user.id, walletName }),
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Erreur génération wallet');
      const newWallet = { id: j.wallet.id, name: j.wallet.name, address: j.wallet.address, isSim: false, fromVPS: true, sol_balance: 0 };
      setWalletList(prev => [...prev, newWallet]);
      setActiveId(newWallet.id);
      setBalances(prev => ({ ...prev, [newWallet.id]: 0 }));
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { setLoading(false); }
  }, [realWallets, isImpersonating]);

  //  Déverrouiller wallet (pour trading manuel depuis navigateur)
  const unlockWallet = useCallback(async (password, walletId = null) => {
    const targetId = walletId || activeId;
    const target = walletList.find(w => w.id === targetId);
    if (!target) { setError('Wallet introuvable'); return false; }
    if (target.isSim) return true;
    // Pour les wallets VPS on simule — le bot VPS gère les clés
    setActiveId(targetId);
    return true;
  }, [walletList, activeId]);

  const lockWallet = useCallback((walletId = null) => {
    if (walletId) {
      setKeypairs(prev => { const n = {...prev}; delete n[walletId]; return n; });
    } else {
      setKeypairs({});
    }
  }, []);

  //  Supprimer un wallet
  const deleteWallet = useCallback(async (walletId = null) => {
    const targetId = walletId || activeId;
    if (targetId === 'sim') return;
    if (isImpersonating) return;
    try {
      await supabase.from('bot_wallets').update({ is_active: false }).eq('id', targetId);
      const newList = walletList.filter(w => w.id !== targetId);
      setWalletList(newList);
      setKeypairs(prev => { const n = {...prev}; delete n[targetId]; return n; });
      setBalances(prev => { const n = {...prev}; delete n[targetId]; return n; });
      if (activeId === targetId) setActiveId(newList[0]?.id || 'sim');
    } catch (e) { setError(e.message); }
  }, [walletList, activeId, isImpersonating]);

  const renameWallet = useCallback(async (walletId, newName) => {
    if (walletId === 'sim') return;
    try {
      await supabase.from('bot_wallets').update({ name: newName }).eq('id', walletId);
      setWalletList(prev => prev.map(w => w.id === walletId ? {...w, name: newName} : w));
    } catch (e) { setError(e.message); }
  }, []);

  const refreshBalance = useCallback(async (walletId = null) => {
    const targets = walletId ? walletList.filter(w => w.id === walletId) : walletList;
    for (const w of targets) {
      if (w.isSim) continue;
      try {
        const conn = getOrCreateConnection();
        const bal = await conn.getBalance(new PublicKey(w.address));
        setBalances(prev => ({ ...prev, [w.id]: bal / LAMPORTS_PER_SOL }));
      } catch {}
    }
  }, [walletList]);

  const setSimBalance = useCallback((amount) => {
    const bal = parseFloat(amount) || 0;
    saveSimBalance(bal);
    setBalances(prev => ({ ...prev, sim: bal }));
  }, []);

  const getKeypair = useCallback((walletId) => {
    return keypairs[walletId] || null;
  }, [keypairs]);

  //  Import wallet (legacy support)
  const importWallet = useCallback(async (privateKey, password, name = null) => {
    setLoading(true); setError(null);
    try {
      if (isImpersonating) throw new Error('Action désactivée en mode lecture seule');
      let kp;
      try { kp = Keypair.fromSecretKey(new Uint8Array(JSON.parse(privateKey))); }
      catch { kp = Keypair.fromSecretKey(Buffer.from(privateKey, 'base64')); }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Non connecté');
      const walletName = name || ('Wallet Importé '+ (realWallets.length + 1));
      const r = await fetch(`${VPS_API}/wallet/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ userId: session.user.id, walletName, privateKeyArray: Array.from(kp.secretKey) }),
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.error);
      const newWallet = { id: j.wallet.id, name: j.wallet.name, address: kp.publicKey.toBase58(), isSim: false, fromVPS: true, sol_balance: 0 };
      setWalletList(prev => [...prev, newWallet]);
      setActiveId(newWallet.id);
      setBalances(prev => ({ ...prev, [newWallet.id]: 0 }));
      return true;
    } catch (e) { setError('Erreur import wallet: ' + e.message); return false; }
    finally { setLoading(false); }
  }, [realWallets, isImpersonating]);

  return {
    wallet, locked, hasWallet, loading, error,
    createWallet, importWallet, unlockWallet, lockWallet, deleteWallet, refreshBalance,
    walletList, activeId, setActiveId, keypairs, balances,
    getKeypair, renameWallet, activeKeypair, isSim, setSimBalance,
    simBalance: balances['sim'] || 0,
  };
}